import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
// @ts-ignore
import { Button, DownloadOutlined, Tooltip } from '@netbasequid/core'
import { Theme, Page } from 'features/ui/types'
import { useDisplayTheme, usePage } from 'hooks/useUiState'
import { useAllWidgetsReady } from 'hooks/useWidgetState'
import { AutomationId } from 'utils/automationUtil'
import SpinningQ from 'components/share/SpinningQ'

import ErrorDialog from './ErrorDialog'
import TooltipContent from './TooltipContent'

const StyledButton = styled(Button)`
  cursor: pointer;
  white-space: nowrap;
`

const StyledContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`

const isChrome = () => {
  const userAgent = window.navigator.userAgent.toLowerCase()
  return userAgent.includes('chrome')
}

type PdfExportButtonProps = {
  handleExport: () => void
}

const PdfExportButton = ({ handleExport }: PdfExportButtonProps) => {
  const theme = useDisplayTheme() as Theme
  const { t } = useTranslation()
  const [exporting, setExporting] = React.useState(false)
  const [showError, setShowError] = React.useState(false)
  const allWidgetsReady = useAllWidgetsReady()
  const page = usePage()

  const onClick = async () => {
    setExporting(true)

    try {
      await handleExport()
    } catch (e) {
      console.error(`Error occurred while exporting dashboard to pdf: ${e}`)
      if (isChrome()) {
        setShowError(true)
      }
    }

    setExporting(false)
  }

  return (
    <>
      <Tooltip
        mode={theme}
        content={<TooltipContent allWidgetsReady={allWidgetsReady} exporting={exporting} />}
        placement="bottom"
        followCursor={false}
        interactive
        offset={[0, 0]}
        enabled={!allWidgetsReady || page !== Page.LiveReport}>
        <div>
          <StyledButton
            mode={theme}
            color="secondary"
            startIcon={
              exporting ? (
                <SpinningQ testId={AutomationId.pdfExport.icon} />
              ) : (
                <DownloadOutlined fontSize={16} />
              )
            }
            onClick={onClick}
            disabled={!allWidgetsReady || exporting}
            data-testid={AutomationId.pdfExport.button}>
            <StyledContent>{exporting ? t('pdfExporting') : t('pdfExport')}</StyledContent>
          </StyledButton>
        </div>
      </Tooltip>
      <ErrorDialog showError={showError} setShowError={setShowError} />
    </>
  )
}

export default PdfExportButton
